/**
 * How Google assembles a responsive search ad for one impression — enough of
 * it to PREVIEW an ad honestly in the Creative Studio.
 *
 * Google shows up to 3 headlines (positions 1–3) and up to 2 descriptions at a
 * time, choosing the combination itself. A headline PINNED to a position only
 * ever appears in that position (several pinned to one position take turns
 * there); unpinned headlines fill the positions that have no pin, in any order.
 * The display URL is the final URL's host plus the display paths.
 */

export interface RsaPreviewContent {
  headlines: ReadonlyArray<{ text: string; pinnedPosition?: 1 | 2 | 3 }>;
  descriptions: ReadonlyArray<{ text: string }>;
  paths?: readonly string[];
  finalUrl: string;
}

export interface RsaCombination {
  /** One headline per shown position, in position order (position 1 first). */
  headlines: string[];
  descriptions: string[];
}

const HEADLINE_SLOTS = [1, 2, 3] as const;
const DESCRIPTIONS_SHOWN = 2;

/** "fias.io/build/free" — the host of the final URL and the display paths. */
export function rsaDisplayUrl(finalUrl: string, paths: readonly string[] = []): string {
  let host = finalUrl;
  try {
    host = new URL(finalUrl).host;
  } catch {
    // Not a URL: show it as written.
  }
  return [host, ...paths.map((p) => p.trim()).filter(Boolean)].join('/');
}

function pick<T>(items: readonly T[], random: () => number): T {
  return items[Math.min(items.length - 1, Math.floor(random() * items.length))];
}

function shuffle<T>(items: readonly T[], random: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.floor(random() * (i + 1)));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * One combination Google could show. With `random` omitted it is the FIRST
 * combination in listed order (pins in their positions, then the unpinned
 * headlines in order) — a stable preview to start from.
 */
export function sampleRsaCombination(
  content: RsaPreviewContent,
  random?: () => number,
): RsaCombination {
  const headlines = content.headlines.filter((h) => h.text.trim());
  const unpinned = headlines.filter((h) => !h.pinnedPosition).map((h) => h.text);
  const pool = random ? shuffle(unpinned, random) : unpinned;
  const shown: string[] = [];
  for (const slot of HEADLINE_SLOTS) {
    const pinned = headlines.filter((h) => h.pinnedPosition === slot).map((h) => h.text);
    if (pinned.length > 0) {
      shown.push(random ? pick(pinned, random) : pinned[0]);
    } else if (pool.length > 0) {
      shown.push(pool.shift()!);
    }
  }
  const descriptions = content.descriptions.map((d) => d.text).filter((t) => t.trim());
  return {
    headlines: shown,
    descriptions: (random ? shuffle(descriptions, random) : descriptions).slice(
      0,
      DESCRIPTIONS_SHOWN,
    ),
  };
}

function combinationKey(c: RsaCombination): string {
  return `${c.headlines.join('\u0000')}\u0001${c.descriptions.join('\u0000')}`;
}

/**
 * A combination different from `previous`, or null when none turned up — the
 * ad has no other (every line pinned, or too few lines to vary). A bounded
 * number of random draws: with any real alternative, 50 misses in a row are
 * vanishingly unlikely.
 */
export function nextRsaCombination(
  content: RsaPreviewContent,
  previous: RsaCombination,
  random: () => number = Math.random,
): RsaCombination | null {
  const seen = combinationKey(previous);
  for (let attempt = 0; attempt < 50; attempt++) {
    const candidate = sampleRsaCombination(content, random);
    if (combinationKey(candidate) !== seen) return candidate;
  }
  return null;
}
